<template>
    <div class="login-root">

        <div class="login-wrapper">

            <!-- Panneau gauche : formulaire -->
            <div class="login-left">

                <!-- Logo -->
                <div class="login-logo">
                    <img src="https://storage.victoranw.fr/file/bqB44gPXyi" class="h-6" alt="TermoFlow" />
                    <span class="login-brand font-bold">TermoFlow</span>
                </div>

                <!-- Form area -->
                <div class="login-form-area">

                    <div class="login-heading">
                        <h1>Connexion</h1>
                        <p>Un lien de connexion vous sera envoyé par e-mail.</p>
                    </div>

                    <form @submit.prevent="sendMagicLink" novalidate class="login-form">

                        <div class="field-group">
                            <label>Adresse e-mail</label>
                            <input
                                v-model.trim="email"
                                type="email"
                                placeholder="exemple@entreprise.fr"
                                :class="{ 'is-error': errors.email }"
                            />
                            <p v-if="errors.email" class="field-error">{{ errors.email }}</p>
                        </div>

                        <button type="submit" :disabled="loading" class="btn-submit">
                            <span v-if="!loading">Se connecter</span>
                            <span v-else class="flex items-center gap-2 justify-center">
                                <svg class="spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="16" height="16">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                                </svg>
                                Envoi en cours...
                            </span>
                        </button>

                        <div v-if="successMessage" class="alert alert-success">
                            <i class="fas fa-check-circle"></i>
                            <div>
                                <strong>Email envoyé !</strong>
                                <p>{{ successMessage }}</p>
                            </div>
                        </div>

                        <div v-if="successMessage" class="alert alert-warn">
                            <i class="fas fa-exclamation-triangle"></i>
                            <p>Pensez à vérifier vos spams.</p>
                        </div>

                        <div v-if="errorMessage" class="alert alert-error">
                            <i class="fas fa-times-circle"></i>
                            <p>{{ errorMessage }}</p>
                        </div>

                    </form>

                    <div class="login-footer">
                        <router-link to="/register" class="btn-register">
                            Créer un compte
                        </router-link>
                        <a href="/" class="btn-back">← Retour à l'accueil</a>
                    </div>

                </div>

            </div>

            <!-- Panneau droit : image -->
            <div class="login-right" :style="rightPanelStyle">
                <div class="login-right-overlay">
                    <div class="login-right-content">
                        <p class="login-right-tagline">La précision au cœur<br>de chaque intervention</p>
                    </div>
                </div>
            </div>

        </div>

    </div>
</template>

<script>
export default {
    name: 'Login',

    props: {
        // Permet de passer une image custom depuis le parent
        // Ex: <Login cover-image="https://..." />
        coverImage: {
            type: String,
            default: 'https://storage.termoflow.fr/file/7GaoRv5dOO'
        }
    },

    data() {
        return {
            email: '',
            errors: {},
            loading: false,
            successMessage: '',
            errorMessage: ''
        };
    },

    computed: {
        rightPanelStyle() {
            return {
                backgroundImage: `url(${this.coverImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            };
        }
    },

    methods: {
        validate() {
            this.errors = {};
            if (!this.email) {
                this.errors.email = "L'adresse e-mail est obligatoire";
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
                this.errors.email = "Le format de l'e-mail est invalide";
            }
            return Object.keys(this.errors).length === 0;
        },

        async sendMagicLink() {
            if (!this.validate()) return;
            this.loading = true;
            this.successMessage = '';
            this.errorMessage = '';

            try {
                let response = await this.$request.post('/auth/magic-link', { email: this.email });

                if (response == 1) {
                    this.$toast.success('Un email vous a été envoyé');
                    this.successMessage = 'Vérifiez votre boîte de réception et cliquez sur le lien pour vous connecter.';
                }
                if (response.error) {
                    this.errorMessage = response.error;
                }
            } catch (error) {
                this.errors.email = 'Une erreur est survenue. Veuillez réessayer plus tard.';
            } finally {
                this.loading = false;
            }
        }
    },

    mounted() {
        if (this.$account && this.$account.account) {
            if (this.$account.account.super_admin == 1) {
                this.$router.push('/admin');
            } else {
                this.$router.push('/app');
            }
        }
    }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');

/* ---- Root ---- */
.login-root {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f1f1ef;
    padding: 24px;
    font-family: 'DM Sans', sans-serif;
}

/* ---- Card wrapper ---- */
.login-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
    max-width: 1000px;
    min-height: 600px;
    background: #fff;
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 8px 48px rgba(0,0,0,0.12);
}

/* ---- Left panel ---- */
.login-left {
    display: flex;
    flex-direction: column;
    padding: 40px 48px;
}

.login-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: auto;
}

.login-brand {
    font-size: 1.25rem;
    color: #111;
}

.login-form-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 40px 0 20px;
}

.login-heading h1 {
    font-size: 2rem;
    color: #111;
    margin: 0 0 6px;
    font-weight: 400;
}

.login-heading p {
    font-size: 0.875rem;
    color: #888;
    margin: 0 0 32px;
}

/* ---- Form ---- */
.login-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.field-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.field-group label {
    font-size: 0.8rem;
    font-weight: 500;
    color: #555;
    letter-spacing: 0.02em;
}

.field-group input {
    padding: 12px 14px;
    border: 1.5px solid #e0e0e0;
    border-radius: 10px;
    font-size: 0.9rem;
    font-family: 'DM Sans', sans-serif;
    color: #111;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    background: #fafafa;
}

.field-group input:focus {
    border-color: #e05a1a;
    box-shadow: 0 0 0 3px rgba(224, 90, 26, 0.1);
    background: #fff;
}

.field-group input.is-error {
    border-color: #ef4444;
    background: #fff5f5;
}

.field-error {
    font-size: 0.78rem;
    color: #ef4444;
    margin: 0;
}

/* ---- Submit button ---- */
.btn-submit {
    padding: 13px;
    background: linear-gradient(135deg, #c0392b, #e05a1a);
    color: #fff;
    border: none;
    border-radius: 10px;
    font-size: 0.95rem;
    font-weight: 600;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.15s, box-shadow 0.2s;
    box-shadow: 0 4px 16px rgba(224, 90, 26, 0.3);
}

.btn-submit:hover:not(:disabled) {
    opacity: 0.92;
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(224, 90, 26, 0.4);
}

.btn-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* ---- Alerts ---- */
.alert {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 10px;
    font-size: 0.83rem;
}

.alert i { margin-top: 2px; flex-shrink: 0; }
.alert p { margin: 0; }
.alert strong { display: block; margin-bottom: 2px; }

.alert-success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534; }
.alert-warn    { background: #fff7ed; border: 1px solid #fed7aa; color: #92400e; }
.alert-error   { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; }

/* ---- Footer ---- */
.login-footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-top: 24px;
}

.btn-register {
    display: block;
    width: 100%;
    text-align: center;
    padding: 12px;
    background: #f4f4f2;
    color: #444;
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 500;
    text-decoration: none;
    transition: background 0.2s;
}

.btn-register:hover { background: #eaeae7; }

.btn-back {
    font-size: 0.8rem;
    color: #aaa;
    text-decoration: none;
    transition: color 0.2s;
}

.btn-back:hover { color: #666; }

/* ---- Right panel ---- */
.login-right {
    position: relative;
    border-radius: 0 20px 20px 0;
    overflow: hidden;
}

.login-right-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(160deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%);
    display: flex;
    align-items: flex-end;
    padding: 40px;
}

.login-right-content {
    color: #fff;
}

.login-right-tagline {
    font-size: 1.75rem;
    font-style: italic;
    line-height: 1.3;
    margin: 0;
    opacity: 0.95;
}

/* ---- Spinner ---- */
.spin {
    animation: spin 0.8s linear infinite;
    display: inline-block;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
}

/* ---- Responsive ---- */
@media (max-width: 768px) {
    .login-root {
        padding: 0;
        align-items: stretch;
    }

    .login-wrapper {
        grid-template-columns: 1fr;
        border-radius: 0;
        min-height: 100vh;
        box-shadow: none;
    }

    /* Image en haut sur mobile */
    .login-right {
        order: -1;
        height: 220px;
        border-radius: 0;
    }

    .login-left {
        padding: 28px 24px 32px;
    }

    .login-logo {
        margin-bottom: 0;
    }

    .login-form-area {
        padding: 28px 0 0;
    }

    .login-heading h1 {
        font-size: 1.6rem;
    }
}
</style>